import React, { useContext, useEffect, useState } from 'react'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import moment from 'moment'
import ChartLine from '../../../../components/charts/ChartLine'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import ChartLineSkeleton from '../../../../components/skeletons/ChartLineSkeleton'
import { formatCurrency, formatDateCard } from '../../../../utils/Helpers'
import { GET_STATUS_CANCELLED_DELIVERED_TRANSLATE } from '../../../../queries/index'
import { DailyMonitoringFiltersContext } from '../../../../context/DailyMonitoringFiltersContext'
import { FilterDailyMonitoring } from '../../../../types/FilterDailyMonitoring'
import { STATUS_CANCELLED, STATUS_DELIVERED, AGG_STATUS_DELIVERED, AGG_STATUS_CANCELLED } from '../../../../types/Order'
import { defaultDateRange, defaultFormatTimeZone } from '../../../../utils/consts'

type BucketStatus = {
  key: string
  doc_count: number
}

type BucketTypes = {
  key_as_string: string,
  key: number,
  doc_count: number,
  cancelled: {
    doc_count : number,
    total : {
      value : number
    }
  },
  delivered: {
    doc_count : number,
    total : {
      value : number
    }
  },
  checkout: {
    doc_count : number,
    total : {
      value : number
    }
  }
}

type Series = {
  name: string
  data: number[]
}

const SalesAverageHistory = (): JSX.Element => {
  const [dateInterval, setDateInterval] = useState('1d')
  const [calendarInterval, setCalendarInterval] = useState('calendar_interval')

  const GET_DATA = gql`
    query SalesHistory($filters: JSON) {
      ordersAggs(
        filters: $filters,
        size: 0,
        query: {
          bool: {
            filter: [
              {
                terms: {
                  status_translate_keyword: [
                    "${STATUS_DELIVERED}",
                    "${STATUS_CANCELLED}"
                  ]
                }
              }
            ]
          }
        },  
        aggs: {
          salesOverTime: {
            date_histogram: {
              field: "created_at",
              ${calendarInterval}: "${dateInterval}",
              time_zone: "-03:00"
            },
            aggs: {
              ${AGG_STATUS_DELIVERED}: {
                filter: {
                  term: {
                    status_translate_keyword: "${STATUS_DELIVERED}"
                  }
                },
                aggs: {
                  total: {
                    sum: {
                      field: "total"
                    }
                  }
                }
              },
              ${AGG_STATUS_CANCELLED}: {
                filter: {
                  term: {
                    status_translate_keyword: "${STATUS_CANCELLED}"
                  }
                },
                aggs: {
                  total: {
                    sum: {
                      field: "total"
                    }
                  }
                }
              }
            }
          }
        }
      ) {
        aggregations
      }
    }
  `

  const { filters } = useContext(DailyMonitoringFiltersContext)
  const categories: Array<string> = []
  const series: Array<Series> = []

  const [getDataStatus, { loading: loadingStatus, error: errorStatus, data: dataStatus }] = useLazyQuery(
    GET_STATUS_CANCELLED_DELIVERED_TRANSLATE, {
      notifyOnNetworkStatusChange: true
    }
  )
  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    getDataStatus()
  }, [])

  useEffect(() => {
    if (!dataStatus) return

    let createdAt: FilterDailyMonitoring['dateRange'] = { ...defaultDateRange }

    if (filters.dateRange) {
      createdAt = {
        ...filters.dateRange,
        ...defaultFormatTimeZone
      }
    }

    const a = moment(createdAt.initialDate)
    const b = moment(createdAt.finalDate)
    const difference = b.diff(a, 'day')

    if (difference >= 0 && difference <= 15) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1d')
    } else if (difference >= 16 && difference <= 91) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1w')
    } else if (difference >= 92 && difference <= 182) {
      setCalendarInterval('fixed_interval')
      setDateInterval('15d')
    } else if (difference >= 183 && difference <= 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1M')
    } else if (difference > 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1q')
    }

    getData({
      variables: { filters: { ...filters, createdAt } }
    })
  }, [dataStatus, filters])

  if (errorStatus || error) return <p>Error :(</p>
  if (loadingStatus || !dataStatus) return <ChartLineSkeleton />
  if (!dataStatus.ordersAggs.aggregations) return <p>No data found</p>

  dataStatus.ordersAggs.aggregations.status.buckets.map((bucket: BucketStatus) => {
    return series.push({ name: bucket.key, data: [] })
  })

  if (networkStatus === NetworkStatus.refetch) return <ChartLineSkeleton />
  if (loading || !data) return <ChartLineSkeleton />
  if (!data.ordersAggs.aggregations.salesOverTime) return <p>No data found</p>

  const { buckets } = data.ordersAggs.aggregations.salesOverTime

  series.map(serie => {
    buckets.map((bucket: BucketTypes) => {
      if (serie.name === STATUS_DELIVERED) {
        serie.data.push(bucket.delivered.total.value)
      } else if (serie.name === STATUS_CANCELLED) {
        serie.data.push(bucket.cancelled.total.value)
      } else {
        serie.data.push(0)
      }
    })
  })

  buckets.map((bucket: BucketTypes) => {
    categories.push(bucket.key_as_string)
  })

  const options = {
    chart: {
      id: 'orders-average-history'
    },
    xaxis: {
      categories,
      type: 'datetime',
      labels: {
        formatter: (value: Date) => {
          return moment(value).format('DD-MM-YYYY')
        }
      }
    },
    yaxis: {
      categories,
      labels: {
        formatter: (value: number) => {
          return formatCurrency(value)
        }
      }
    }
  }

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>
      Média de vendas ({formatDateCard(filters.dateRange?.initialDate, filters.dateRange?.finalDate)})
      {dateInterval === '1d'
        ? (<Typography variant='caption'> filtrado por dia</Typography>)
        : dateInterval === '1w'
          ? (<Typography variant='caption'> filtrado por semana</Typography>)
          : dateInterval === '15d'
            ? (<Typography variant='caption'> filtrado por quinzena</Typography>)
            : dateInterval === '1M'
              ? (<Typography variant='caption'> filtrado por mês</Typography>)
              : dateInterval === '1q'
                ? (<Typography variant='caption'> filtrado por trimestre</Typography>)
                : ''
      }
    </Typography>
    <ChartLine options={options} series={series} theme={ChartLineThemes.tworeverse} height={320} type="area" />
  </Paper>
}

export default SalesAverageHistory
